import {Col, Row} from 'antd';
import React, {useCallback, useEffect, useMemo, useRef} from 'react';
import {Elements} from '@stripe/react-stripe-js';
import SponsorOptionPageHeroText from '../../CommonComponents/SponsorOptionPageHeroText';
import SectionLayout from '../../CommonComponents/Layout/sectionLayout';
import NavigationComponent from '../../CommonComponents/CommonBackButton';
import CommonPaymentCheckoutForm from '../../CommonComponents/CommonPaymentCheckoutForm';
import {
  createStipePromise,
  SponsorshipType,
} from '../../../Util/commonUtility';
import {applicationConfig} from '../../../Util/config';
import {useDispatch, useSelector} from 'react-redux';
import {useNavigate, useParams} from 'react-router-dom';
import {
  postEventPaymentSuccess,
  resetEventPayment,
  normalRegistrationByUserAndEventId,
  sponsorRegistrationByUserAndEventId,
  readAddedRepsParent,
} from '../../../Store/Actions/eventRegister';
import useLocalization from '../../../Localization';
import PaymentRegistrationSummary from './PaymentRegistrationSummary';
import './PaymentRegistrationSummary/style.css';

const BANK_RELATED_PAYMENT_METHOD_TYPES = new Set([
  'acss_debit',
  'au_becs_debit',
  'bacs_debit',
  'customer_balance',
  'nz_bank_account',
  'sepa_debit',
  'us_bank_account',
]);

const isProcessingFeeEligiblePaymentMethod = (methodType) => {
  if (!methodType) return true;
  const normalizedMethodType = String(methodType).trim().toLowerCase();
  const compactMethodType = normalizedMethodType.replace(/[^a-z0-9]/g, '');
  return (
    !BANK_RELATED_PAYMENT_METHOD_TYPES.has(normalizedMethodType) &&
    !compactMethodType.includes('bank') &&
    !compactMethodType.includes('debit')
  );
};

const PaymentCheckoutComponent = ({
  localChapterId,
  totalCartPrice,
  getAttendeeByEventId,
  includeProcessingFee,
  setIncludeProcessingFee,
  attendeesData,
  sponsorData,
  isSponsorCheckout,
  eventLocalChapterData,
  appliedCartCoupon,
  eventType,
  eventISOnline,
}) => {
  const {id, attendeeType, optionId} = useParams();
  const addedRepsParentId =
    attendeeType === 'sponsor' ? readAddedRepsParent() : null;
  const {ApplicationString} = useLocalization();
  const [isCardPaymentMethod, setIsCardPaymentMethod] = React.useState(true);
  const [selectedPaymentMethodType, setSelectedPaymentMethodType] =
    React.useState('card');
  const [isIntentRefreshing, setIsIntentRefreshing] = React.useState(false);
  const lastSyncedFeePreferenceRef = useRef(includeProcessingFee);
  const isRefreshingIntentRef = useRef(false);
  /** null = unknown (new intent), true = fee applies, false = bank/direct debit */
  const stripePaymentIsCardRef = useRef(null);
  /** Ignore fee-restore immediately after a new clientSecret (spurious Stripe events). */
  const resumeCardFeeRestoreAfterRef = useRef(0);
  const includeProcessingFeeRef = useRef(includeProcessingFee);
  const attendeeSponsorType = localStorage.getItem('attendeeSponsorType');
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {clientSecret, publishable_key} = useSelector(
    ({eventRegister}) => eventRegister?.eventRegistrationClientKeys,
  );
  includeProcessingFeeRef.current = includeProcessingFee;

  const resolvedPublishableKey =
    publishable_key || applicationConfig.STRIPE_PROMISE_KEY || '';
  const stripePromise = useMemo(
    () =>
      resolvedPublishableKey
        ? createStipePromise(resolvedPublishableKey)
        : null,
    [resolvedPublishableKey],
  );
  const stripeOptions = useMemo(
    () => ({
      clientSecret,
      appearance: {theme: 'stripe'},
    }),
    [clientSecret],
  );

  useEffect(() => {
    if (!clientSecret) {
      navigate('/');
    }
  }, [clientSecret]);

  useEffect(() => {
    // Only clear tab memory when the new intent still includes the processing fee. After bank
    // methods we turn the fee off — keep ref `false` so bank→fee-bearing methods can restore the
    // checkbox even if Stripe does not send another non-bank event for the new intent.
    if (includeProcessingFeeRef.current) {
      stripePaymentIsCardRef.current = null;
    }
    resumeCardFeeRestoreAfterRef.current = Date.now() + 450;
  }, [clientSecret]);

  // Fetch summary once on entry. Do not refetch when the fee checkbox toggles —
  // that re-renders the sponsor/vendor tables and collapses the Stripe iframe.
  useEffect(() => {
    if (id) {
      getAttendeeByEventId(id);
    }
  }, [id]);

  useEffect(() => {
    if (!id) return;
    if (includeProcessingFee === lastSyncedFeePreferenceRef.current) return;
    if (isRefreshingIntentRef.current) return;
    isRefreshingIntentRef.current = true;
    setIsIntentRefreshing(true);

    const refreshAction =
      attendeeType === 'sponsor'
        ? sponsorRegistrationByUserAndEventId(
            id,
            navigate,
            false,
            optionId,
            includeProcessingFee,
            true,
          )
        : normalRegistrationByUserAndEventId(
            id,
            navigate,
            false,
            includeProcessingFee,
            true,
          );

    Promise.resolve(dispatch(refreshAction)).finally(() => {
      lastSyncedFeePreferenceRef.current = includeProcessingFee;
      isRefreshingIntentRef.current = false;
      setIsIntentRefreshing(false);
    });
  }, [id, attendeeType, includeProcessingFee, optionId]);

  const dispatchSuccess = useCallback(
    (eventId, intent) => {
      dispatch(
        postEventPaymentSuccess(
          {
            eventId,
            paymentIntentId: intent,
            type: attendeeType,
          },
          navigate,
        ),
      );
    },
    [attendeeType, dispatch, navigate],
  );

  const renderExtra = useCallback(() => {
    if (attendeeType === 'sponsor') {
      return Number(attendeeSponsorType) === SponsorshipType.MAJOR
        ? ApplicationString['paymentCheckoutComponent.sponsor.major.extra']
        : ApplicationString['paymentCheckoutComponent.sponsor.non-major.extra'];
    }
    return ApplicationString['paymentCheckoutComponent.registration.extra'];
  }, [attendeeType, attendeeSponsorType]);

  const handlePaymentMethodChange = useCallback(
    (methodType) => {
      // Ignore empty type during Stripe UI transitions (avoids incorrect fee toggles).
      if (!methodType) return;
      setSelectedPaymentMethodType(methodType);
      const nowCard = isProcessingFeeEligiblePaymentMethod(methodType);
      const prevIsCard = stripePaymentIsCardRef.current;

      if (!nowCard && includeProcessingFeeRef.current) {
        setIncludeProcessingFee(false);
      }
      // Restore 2.9% when Stripe reports a fee-bearing method after a bank tab (`ref === false`).
      // Debounce right after a new clientSecret so an immediate stray event
      // does not toggle the fee (see bank-transfer selection bug).
      if (
        nowCard &&
        prevIsCard === false &&
        Date.now() >= resumeCardFeeRestoreAfterRef.current
      ) {
        setIncludeProcessingFee(true);
      }

      setIsCardPaymentMethod(nowCard);
      stripePaymentIsCardRef.current = nowCard;
    },
    [setIncludeProcessingFee],
  );

  const isSummaryReady = isSponsorCheckout
    ? Boolean(
        (sponsorData &&
          !Array.isArray(sponsorData) &&
          Object.keys(sponsorData).length) ||
          attendeesData?.length,
      )
    : Boolean(attendeesData?.length);
  // Mount Stripe only after summary rows exist so tables above do not
  // insert/reflow after the Payment Element iframe has sized itself.
  const canMountStripe =
    Boolean(clientSecret) && Boolean(stripePromise) && isSummaryReady;

  return (
    <React.Fragment>
      <Row
        gutter={[0, 12]}
        className='pbssMainContainerRow payment-checkout-page'>
        <Col span={24}>
          <SponsorOptionPageHeroText
            title={'Payment'}
            subTitle={'Check your payment details'}
          />
        </Col>
        <Col span={24}>
          <SectionLayout>
            <NavigationComponent
              name={
                addedRepsParentId
                  ? ApplicationString[
                      'paymentCheckoutComponent.backToAddVendorReps'
                    ]
                  : attendeeType === 'sponsor'
                  ? ApplicationString[
                      'userEventSponsorCartComponent.backToSponsorship'
                    ]
                  : ApplicationString[
                      'paymentCheckoutComponent.backToAddAttendees'
                    ]
              }
              urlPath={
                addedRepsParentId
                  ? `addVendorReps/${id}/${addedRepsParentId}`
                  : attendeeType === 'sponsor'
                  ? `eventSponsor/${id}/${optionId}`
                  : `addAttendees/${id}`
              }
            />
          </SectionLayout>
        </Col>
        <Col span={24}>
          <SectionLayout extendClass='payment-checkout-overflow-visible'>
            <PaymentRegistrationSummary
              attendeesData={attendeesData}
              sponsorData={sponsorData}
              isSponsorCheckout={isSponsorCheckout}
              totalCartPrice={totalCartPrice}
              appliedCartCoupon={appliedCartCoupon}
              eventLocalChapterData={eventLocalChapterData}
              eventType={eventType}
              eventISOnline={eventISOnline}
            />
          </SectionLayout>
        </Col>
        <Col span={24}>
          {canMountStripe ? (
            <div className='payment-checkout-stripe-host'>
              <Elements
                key={clientSecret}
                options={stripeOptions}
                stripe={stripePromise}>
                <div className='card-stripe-container'>
                  <CommonPaymentCheckoutForm
                    clientSecret={clientSecret}
                    price={totalCartPrice?.discounted_price}
                    DataId={parseInt(id, 10)}
                    resetPayment={resetEventPayment}
                    dispatchSuccess={dispatchSuccess}
                    successUrl={'/paymentSuccess/myEvents'}
                    skipNavigateFromHook
                    extra={renderExtra()}
                    localChapterId={localChapterId}
                    isProcessingFeeAdded={includeProcessingFee}
                    onProcessingFeeToggle={setIncludeProcessingFee}
                    isCardPaymentMethod={isCardPaymentMethod}
                    selectedPaymentMethodType={selectedPaymentMethodType}
                    onPaymentMethodChange={handlePaymentMethodChange}
                    disablePaymentSubmit={
                      isIntentRefreshing ||
                      includeProcessingFee !==
                        lastSyncedFeePreferenceRef.current
                    }
                    isIntentRefreshing={isIntentRefreshing}
                  />
                </div>
              </Elements>
            </div>
          ) : null}
        </Col>
      </Row>
    </React.Fragment>
  );
};

export default PaymentCheckoutComponent;
